import { Injectable, Signal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RuntimeConfigService } from '../common/runtime-config.service';
import type { ApiResponse } from '../../../types/commons';
import type { ConversionEngineOption } from '../../../types/conversion-engine';

/**
 * Owns `GET /{apiVersion}/mbrs/conversion-engines`: the list of conversion
 * engines this deployment lets a user choose between.
 *
 * Three properties matter, and each is load-bearing:
 *
 * 1. **The flag suppresses the call, not just the UI.** `ensureLoaded()`
 *    returns without touching the network when
 *    `RuntimeConfigService.enginePickerEnabled()` is false. This is the ONLY
 *    place that requirement is enforced — do not move the check into the
 *    picker component, or a future second caller will reintroduce the call.
 *
 * 2. **Failure degrades to an empty list, never to a guess.** Unlike
 *    `FilingSessionLimitsService`, which seats a hardcoded fallback, there is
 *    no safe default set of engines: which ones a deployment offers is exactly
 *    what this call exists to discover. An empty list makes the picker render
 *    nothing, submits omit the engine field, and the server applies its own
 *    configured default — i.e. the pre-feature behaviour. A warning goes to the
 *    console; deliberately no toast, because an auxiliary call must not put a
 *    red banner on a page the user came to file on.
 *
 * 3. **The list is deployment-scoped**, not per-user or per-conversion, so one
 *    fetch per app lifetime is correct and it survives a logout.
 *
 * The route is auth-guarded, so `ensureLoaded()` is called from the picker
 * component's `ngOnInit` (which only ever mounts behind `isAuthenticatedGuard`),
 * never from `APP_INITIALIZER` — a boot-time fetch would 401 on the login page.
 */
@Injectable({ providedIn: 'root' })
export class ConversionEngineService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);

  private readonly optionsSignal = signal<ConversionEngineOption[]>([]);
  private readonly isLoadedSignal = signal<boolean>(false);

  /**
   * Empty until a successful fetch, and empty forever when the flag is off or
   * the fetch failed. Consumers treat empty as "render nothing".
   */
  readonly options: Signal<ConversionEngineOption[]> = this.optionsSignal.asReadonly();

  private inFlight = false;

  /**
   * Idempotent. Fires the GET at most once per app lifetime. A failed attempt
   * is retried on the next call (i.e. the next picker mount); a successful one
   * is never refetched.
   */
  ensureLoaded(): void {
    if (!this.config.enginePickerEnabled()) {
      return;
    }
    if (this.isLoadedSignal() || this.inFlight) {
      return;
    }
    this.inFlight = true;

    const url = `${this.config.apiUrl()}/${this.config.apiVersion()}/mbrs/conversion-engines`;

    this.http
      .get<ApiResponse<ConversionEngineOption[]>>(url, { withCredentials: true })
      .subscribe({
        next: (response) => {
          this.inFlight = false;
          const parsed = parseEngineOptions(response?.data);
          if (parsed === null) {
            this.warnUnavailable('the response payload was malformed');
            return;
          }
          this.optionsSignal.set(parsed);
          this.isLoadedSignal.set(true);
        },
        error: (err: unknown) => {
          this.inFlight = false;
          this.warnUnavailable(
            `the request failed (${err instanceof Error ? err.message : String(err)})`,
          );
        },
      });
  }

  private warnUnavailable(reason: string): void {
    console.warn(
      `[conversion-engine] The engine picker will not render because ${reason}. ` +
        `Conversions will run on the server's configured default engine, which is ` +
        `the same behaviour as when the enginePickerEnabled flag is off.`,
    );
  }
}

/**
 * Validates the wire payload before trusting it.
 *
 * Returns `null` for anything malformed — which the caller treats exactly like
 * a transport failure. A partially-valid list is worse than none: it would put
 * a dropdown in front of the user that is missing the engine they wanted, with
 * no indication anything went wrong.
 *
 * An EMPTY array is valid and distinct from `null`: it means the deployment
 * offers no choice, which is a legitimate answer.
 */
function parseEngineOptions(data: unknown): ConversionEngineOption[] | null {
  if (!Array.isArray(data)) {
    return null;
  }

  const parsed: ConversionEngineOption[] = [];

  for (const entry of data) {
    if (entry === null || typeof entry !== 'object') {
      return null;
    }
    const raw = entry as Record<string, unknown>;
    const value = raw['value'];
    const label = raw['label'];
    const description = raw['description'];
    const isDefault = raw['isDefault'];

    if (typeof value !== 'string' || value.length === 0) {
      return null;
    }
    if (typeof label !== 'string' || label.length === 0) {
      return null;
    }
    if (typeof isDefault !== 'boolean') {
      return null;
    }

    parsed.push({
      value,
      label,
      // Sub-text is presentational only, so a missing one degrades to empty
      // rather than rejecting an otherwise usable option.
      description: typeof description === 'string' ? description : '',
      isDefault,
    });
  }

  return parsed;
}
